import { Component, Signal, signal, ChangeDetectionStrategy, inject, Inject } from '@angular/core';

import { CustomDatePickerComponent, ModalLayoutComponent } from '@program-suite/ui';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UI_FORM_IMPORTS } from '@program-suite/ui';
import { ButtonComponent } from '@program-suite/ui';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LookupViewModel, PolicyChangeStatusEnum } from '@program-suite/data-common';
import { LookupStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { PolicyEndorsementsService } from '@program-suite/data-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent, ToastType } from '@program-suite/ui';
import { Mode } from '../endorsement.utility';

@Component({
  selector: 'lib-policy-endorsement-form',
  templateUrl: './policy-endorsement-form.component.html',
  styleUrl: './policy-endorsement-form.component.scss',
  imports: [
    ModalLayoutComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    ...UI_FORM_IMPORTS,
    CustomDatePickerComponent
],
})
export class PolicyEndorsementFormComponent {
  // required data policyChangeGuid, policyChangeStatus (if existing), policyChangeType, policyNumber, insuredName
  data: any = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<PolicyEndorsementFormComponent>)
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  reinstatementForm: FormGroup;
  cancellationForm: FormGroup;
  otherForm: FormGroup;
  form: FormGroup;
  successMessage = '';
  title = signal('');
  mode: 'view' | 'edit' | 'add';
  policyNumber = this.data.policyNumber;
  insuredName = this.data.insuredName;
  amendmentTypes = [
    { id: 'No Changes', name: 'No Changes' },
    { id: 'To Be Adjusted at Audit', name: 'To Be Adjusted at Audit' },
    { id: 'Additional Premium', name: 'Additional Premium' },
    { id: 'Return Premium', name: 'Return Premium' },
  ];
  cancellationTypes = [
    { id: '8', name: 'Notice of Cancellation' },
    { id: '9', name: 'Cancellation' },
    { id: '12', name: 'Notice of Non-Renewal' },
    { id: '18', name: 'Flat Cancellation' },
  ];

  constructor(
        private snackBar: MatSnackBar,
        private policyEndorsementsService: PolicyEndorsementsService,
  ) {
    this.reinstatementForm = new FormGroup({
      reinstatementReason: new FormControl(null, Validators.required),
      reinstatementNotes: new FormControl(null),
      typeOfEndorsement: new FormControl(11),
    });

    this.cancellationForm = new FormGroup({
      cancellationReason: new FormControl(null, Validators.required),
      cancellationEffectiveDate: new FormControl(new Date(), Validators.required),
      cancellationNotes: new FormControl(null),
      typeOfEndorsement: new FormControl(9),
    });

    this.otherForm = new FormGroup({
      otherEffectiveDate: new FormControl(new Date(), Validators.required),
      description: new FormControl(null, Validators.required),
      amendment: new FormControl(null),
      amount: new FormControl(null),
      typeOfEndorsement: new FormControl(14),
      file: new FormControl(null),
    });
    this.mode = this.determineModalMode(this.data.policyChangeStatus);
    switch (this.data.policyChangeType) {
      case 'Cancellation':
      case 'NoticeOfCancellation':
        this.lookupStore.loadLookup('cancellationReasons');
        this.form = this.cancellationForm;
        this.successMessage = 'Cancellation Request Submitted';
        this.title.set(this.authStore.isAdmin() ? 'Cancellation' : 'Cancellation Request');
        break;
      case 'Reinstatement':
        this.lookupStore.loadLookup('reinstatementReasons');
        this.form = this.reinstatementForm;
        this.successMessage = 'Reinstatement Request submitted';
        this.title.set(this.authStore.isAdmin() ? 'Reinstatement' : 'Reinstatement Request');
        break;
      case 'Other':
        this.form = this.otherForm;
        this.successMessage = 'Endorsement Issued';
        this.title.set('Other Endorsement');
        break;
      default:
        this.lookupStore.loadLookup('cancellationReasons');
        this.form = this.cancellationForm;
        this.successMessage = 'Cancellation Request Submitted';
        this.title.set(this.authStore.isAdmin() ? 'Cancellation' : 'Cancellation Request');
        break;
    }

    if (this.mode === 'edit' || this.mode === 'view') {
      const deStringifyJson = JSON.parse(this.data.formJSON);
      if (this.mode === 'view') {
        this.form.disable();
      }
      this.insuredName = deStringifyJson.insuredName;
      this.policyNumber = deStringifyJson.policyNumber;
      this.form.patchValue({
        ...deStringifyJson,
      });
    }

    this.otherForm.get('amendment')?.valueChanges.subscribe((value) => {
      if (value === 'Additional Premium' || value === 'Return Premium') {
        this.otherForm.get('amount')?.setValidators(Validators.required);
      } else {
        this.otherForm.get('amount')?.clearValidators();
      }

      this.otherForm.get('amount')?.updateValueAndValidity();
    });

    this.cancellationForm.get('typeOfEndorsement')?.valueChanges.subscribe((value) => {
      if (value === 18) {
        this.cancellationForm.get('cancellationEffectiveDate')?.setErrors(null);
      } else {
        this.cancellationForm.get('cancellationEffectiveDate')?.setValidators(Validators.required);
      }
    });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid && this.mode !== 'view') {
      const form = this.formatFormData();
      this.policyEndorsementsService.submitPolicyChangeRequest(form)
        .then((response: string) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: this.successMessage }
          });
          this.dialogRef.close(true);
        })
        .catch((error: any) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Error, message: error.error }
          });
        });
    }
  }

  onIssue() {
    this.form.markAllAsTouched();
    if (this.form.valid && this.mode !== 'view' && this.authStore.isAdmin()) {
      const form = this.formatFormData();
      this.policyEndorsementsService.issuePolicyChangeRequest(form)
        .then((response: string) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: this.successMessage }
          });
          this.dialogRef.close(true);
        })
        .catch((error: any) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Error, message: error.error }
          });
        });
    }
  }

  onDecline() {
    this.form.markAllAsTouched();
    if (this.form.valid && this.mode !== 'view' && this.authStore.isAdmin()) {
      const form = this.formatFormData();
      this.policyEndorsementsService.declinePolicyChangeRequest(form)
        .then((response: string) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: this.successMessage }
          });
          this.dialogRef.close(true);
        })
        .catch((error: any) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Error, message: error.error }
          });
        });
    }
  }

  formatFormData() {
    const formData = this.form.value;
    const formJson = {
      ...formData,
      policyNumber: this.policyNumber,
      insuredName: this.insuredName,
    }
    const form = new FormData();
    form.append('formJSON', JSON.stringify(formJson));
    form.append('policyGuid', this.data.policyGuid);
    form.append('policyChangeType', formJson.typeOfEndorsement);
    if (this.mode !== 'add') {
      form.append('policyChangeGuid', this.data.policyChangeGuid);
    } else if (this.data.policyChangeType === 'Other' && this.mode == 'add') {
    const files = this.form.get('file')?.value;
    if(files){
      for (let i = 0; i < files.length; i++) {
        form.append('documents', files[i]);
      }
    }
    
    }
    return form;
  }

  determineModalMode(policyChangeStatus: PolicyChangeStatusEnum): Mode {
    const authStore = inject(AuthStore);
    // if admin always edit, unless already issued or declined
    // otherwise, if draft edit
    // otherwise view only
  
    if (policyChangeStatus === null || policyChangeStatus === undefined) {
      return 'add';
    }
  
    if (
      policyChangeStatus === PolicyChangeStatusEnum.Issued ||
      policyChangeStatus === PolicyChangeStatusEnum.Declined ||
      policyChangeStatus === PolicyChangeStatusEnum.Voided
    ) {
      return 'view';
    }
  
    if (authStore.isAdmin()) {
      return 'edit';
    }
  
    if (policyChangeStatus === PolicyChangeStatusEnum.Draft) {
      return 'edit';
    } else {
      return 'view';
    }
  }
}
